export function toSafeDate(
  date: string | Date | null | undefined,
): Date | null {
  if (date == null) return null;
  const d = typeof date === "string" ? new Date(date) : date;
  return isNaN(d.getTime()) ? null : d;
}

export function formatShortDate(
  date: string | Date | null | undefined,
  locale: string,
): string | null {
  const d = toSafeDate(date);
  if (!d) return null;
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function formatDateOnly(dateString: string, locale: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

export function formatDate(dateString: string, locale: string): string {
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
  const timePart = date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${datePart} ${timePart}`;
}

export function formatDateTime(
  date: string | Date | null | undefined,
  locale: string,
): string | null {
  const d = toSafeDate(date);
  if (!d) return null;
  return d.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatWeekdayShortDate(
  date: string | Date | null | undefined,
  locale: string,
): string | null {
  const d = toSafeDate(date);
  if (!d) return null;
  return d.toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

export function formatYear(
  date: string | Date | null | undefined,
): string | null {
  const d = toSafeDate(date);
  if (!d) return null;
  return String(d.getFullYear());
}

export function formatRelativeDate(
  date: string | Date | null | undefined,
  locale: string,
): string {
  const d = toSafeDate(date);
  if (!d) {
    return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
      0,
      "second",
    );
  }

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffMonths = Math.floor(diffDays / 30);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "always" });

  if (diffMonths > 0) return rtf.format(-diffMonths, "month");
  if (diffDays > 0) return rtf.format(-diffDays, "day");
  if (diffHours > 0) return rtf.format(-diffHours, "hour");
  if (diffMinutes > 0) return rtf.format(-diffMinutes, "minute");
  return new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
    0,
    "second",
  );
}

export function getTimeZoneLabel(
  dateString: string,
  locale: string,
): string {
  const date = new Date(dateString);
  const parts = new Intl.DateTimeFormat(locale, {
    timeZoneName: "short",
  }).formatToParts(date);
  return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
}

export function formatDateWithZone(
  dateString: string,
  locale: string,
): string {
  const base = formatDate(dateString, locale);
  const zone = getTimeZoneLabel(dateString, locale);
  return zone ? `${base} ${zone}` : base;
}
